import { Button, Modal } from '@nexo/ui';
import { Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { formatLongDate } from '../../lib/format';
import { useMe } from '../auth/AuthProvider';
import { ReleaseNotes } from './ReleaseNotes';
import { unseenEntries, useChangelog, useMarkSeen } from './useChangelog';

/** At most this many versions in the pop-up; the rest are in «Novedades». */
const MAX_SHOWN = 3;

/** Pop-up with what changed since the user last looked (once per version, on any device). */
export function WhatsNewDialog() {
  const { t } = useTranslation();
  const me = useMe();
  const log = useChangelog().data ?? [];
  const markSeen = useMarkSeen();
  const [dismissed, setDismissed] = useState(false);
  const latest = log[0];
  const unseen = unseenEntries(log, me);

  // People who signed up after the latest version: nothing is news, remember that quietly.
  const quietVersion =
    latest && me.lastSeenVersion === null && unseen.length === 0 ? latest.version : null;
  useEffect(() => {
    if (quietVersion && !markSeen.isPending) markSeen.mutate(quietVersion);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when that state appears
  }, [quietVersion]);

  if (!latest || unseen.length === 0 || dismissed) return null;
  const close = () => {
    setDismissed(true);
    markSeen.mutate(latest.version);
  };

  return (
    <Modal
      open
      onClose={close}
      title={t('Novedades de Nexo')}
      description={
        unseen.length === 1
          ? t('Lo nuevo en la versión {{version}}', { version: latest.version })
          : t('Lo nuevo desde tu última visita')
      }
      size="md"
    >
      <div className="scrollbar-thin -mx-1 flex max-h-[60vh] flex-col gap-5 overflow-y-auto px-1">
        {unseen.slice(0, MAX_SHOWN).map((entry) => (
          <section key={entry.version}>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <Sparkles className="size-4 text-highlight" />
              {t('Versión {{version}}', { version: entry.version })}
              <span className="text-xs font-normal text-subtle">
                {formatLongDate(entry.publishedAt)}
              </span>
            </h3>
            <ReleaseNotes notes={entry.notes} />
          </section>
        ))}
      </div>
      <div className="mt-5 flex items-center justify-between gap-2 border-t border-border pt-4">
        <Link to="/app/news" onClick={close} className="text-sm text-accent hover:underline">
          {unseen.length > MAX_SHOWN ? t('Ver todas las novedades') : t('Ver el historial')}
        </Link>
        <Button onClick={close}>{t('Entendido')}</Button>
      </div>
    </Modal>
  );
}
